import React from 'react';
import PropTypes from 'prop-types';
import useAuth from 'src/hooks/useAuth';
import useStore from 'src/hooks/useStore';
import useSource from 'src/hooks/useSource';
import useUser from 'src/hooks/useUser';
import useVehicle from 'src/hooks/useVehicle';
import useComment from 'src/hooks/useComment';
import useMake from 'src/hooks/useMake';
import useAppointment from 'src/hooks/useAppointment';
import useFinancialInstitution from 'src/hooks/useFinancialInstitution';
import useDocument from 'src/hooks/useDocument';
import { extractStoreIds } from 'src/modules/store/helpers';
import moment from 'moment';
import { authorize } from 'src/utils/auth';
import {
    ADMIN_LEVEL_ACCESS,
    DEVELOPER_LEVEL_ACCESS,
    GROUP_LEVEL_ACCESS,
    USER_LEVEL_ACCESS,
} from './access';

const DPXWrapper = ({ children, props }) => {
    const { user } = useAuth();

    const { getSources } = useSource();
    const { getAgents } = useUser();
    const { getStores } = useStore();
    const { getMakes } = useMake();
    const { getFinancialInstitutions } = useFinancialInstitution();
    const { getVehicles } = useVehicle();
    const { getDocuments, getDocumentsByMultiStore } = useDocument();
    const { getAppointmentsByStore, getAppointmentsByUser, clearState } =
        useAppointment();

    const getDocumentsDPX = async ({ role, store }) => {
        if (
            authorize(user?.accessLevel, GROUP_LEVEL_ACCESS, ADMIN_LEVEL_ACCESS)
        ) {
            await getDocumentsByMultiStore(
                `&store[in]=${extractStoreIds(store)}`
            );
        } else if (authorize(user?.accessLevel, DEVELOPER_LEVEL_ACCESS)) {
            await getDocuments();
        }
    };

    const getAppointmentsDPX = async ({ role, id, store }) => {
        if (
            authorize(user?.accessLevel, USER_LEVEL_ACCESS) ||
            role === 'agent'
        ) {
            clearState();
            await getAppointmentsByUser(
                id,
                `&startDate[lt]=${moment(new Date())
                    .add(15, 'days')
                    .format('l')}&lead[exists]=true`
            );
        } else if (
            authorize(user?.accessLevel, GROUP_LEVEL_ACCESS, ADMIN_LEVEL_ACCESS)
        ) {
            clearState();
            await getAppointmentsByStore(
                `&store[in]=${extractStoreIds(store)}&startDate[lt]=${moment(
                    new Date()
                )
                    .add(15, 'days')
                    .format('l')}&lead[exists]=true`
            );
        } else if (role === 'store') {
            clearState();
            await getAppointmentsByStore(
                `&store[in]=${store}&startDate[lt]=${moment(new Date())
                    .add(15, 'days')
                    .format('l')}&lead[exists]=true`
            );
        }
    };

    const getFinancialInstitutionsDPX = async (role) => {
        if (
            authorize(
                user?.accessLevel,
                GROUP_LEVEL_ACCESS,
                ADMIN_LEVEL_ACCESS,
                USER_LEVEL_ACCESS
            )
        ) {
            await getFinancialInstitutions(
                `&isActive=true&group=${user.group._id}`
            );
        } else if (authorize(user?.accessLevel, DEVELOPER_LEVEL_ACCESS)) {
            await getFinancialInstitutions(`&isActive=true`);
        }
    };

    const getSourcesDPX = async (role, groupId, isActive = true) => {
        let act = '';

        if (isActive) act = '&isActive=true';
        if (
            authorize(
                user?.accessLevel,
                GROUP_LEVEL_ACCESS,
                ADMIN_LEVEL_ACCESS,
                USER_LEVEL_ACCESS
            )
        ) {
            await getSources(`${act}&group=${user?.group?._id}&isActive=true`);
        } else if (authorize(user?.accessLevel, DEVELOPER_LEVEL_ACCESS)) {
            await getSources(``);
        } else if (role === 'group') {
            await getSources(`${act}&group=${groupId}&isActive=true`);
        }
    };

    const getRolesDPX = async ({ role, group }) => {
        if (
            authorize(
                user?.accessLevel,
                GROUP_LEVEL_ACCESS,
                ADMIN_LEVEL_ACCESS,
                USER_LEVEL_ACCESS
            )
        ) {
            await getRolesByGroup(user.group._id);
        } else if (authorize(user?.accessLevel, DEVELOPER_LEVEL_ACCESS)) {
            await getAllRoles();
        } else if (role === 'group') {
            await getRolesByGroup(group);
        }
    };

    const childrenDpxProps = React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
            return React.cloneElement(child, {
                ...props,
                getStores,
                getMakes,
                getSources: getSourcesDPX,
                getAgents,
                getVehicles,
                getAppointments: getAppointmentsDPX,
                getFinancialInstitutions: getFinancialInstitutionsDPX,
                getDocuments: getDocumentsDPX,
                getRoles: getRolesDPX,
            });
        }
        return child;
    });

    return <>{childrenDpxProps}</>;
};

DPXWrapper.propTypes = {
    children: PropTypes.node,
};

export default DPXWrapper;
